(() => {
  const compact = window.matchMedia('(max-width: 989px)');
  const initialized = new WeakSet();

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const slot = root.querySelector('[data-bb-mobile-variants]');
    const origin = root.querySelector('[data-bb-variant-origin]');
    const bar = root.querySelector('[data-bb-mobile-buy]');
    let galleryBinding;

    const placePicker = () => {
      const picker = root.querySelector('variant-selects');
      if (!slot || !origin || !picker) return;
      if (compact.matches && picker.parentElement !== slot) slot.append(picker);
      if (!compact.matches && picker.previousElementSibling !== origin) origin.after(picker);
    };

    placePicker();
    compact.addEventListener('change', placePicker);

    const bindGallery = (gallery) => {
      if (!gallery) return { refresh() {}, disconnect() {} };
      const controls = gallery.querySelector('[data-bb-gallery-controls]');
      const count = gallery.querySelector('[data-bb-gallery-count]');
      const previous = gallery.querySelector('[data-bb-gallery-prev]');
      const next = gallery.querySelector('[data-bb-gallery-next]');
      if (!controls || !count || !previous || !next) return { gallery, refresh() {}, disconnect() {} };
      const dots = document.createElement('span');
      dots.className = 'bb-product-gallery__dots';
      dots.setAttribute('aria-label', 'Productbeelden');
      controls.append(dots);
      let currentMediaId = null;

      const mediaItems = () => [...gallery.querySelectorAll('[id^="Slider-Gallery-"] > [data-media-id]')];
      const refresh = (mediaId) => {
        const items = mediaItems();
        const tallestRatio = items.reduce((ratio, item) => {
          const padding = Number.parseFloat(item.style.getPropertyValue('--bb-gallery-image-padding'));
          return Number.isFinite(padding) ? Math.max(ratio, padding / 100) : ratio;
        }, 1.25);
        gallery.style.setProperty('--bb-gallery-width-scale', String(1 / tallestRatio));
        const active = mediaId || gallery.querySelector('.thumbnail-list [aria-current="true"]')?.closest('[data-target]')?.dataset.target
          || gallery.querySelector('.product__media-item.is-active')?.dataset.mediaId;
        let index = items.findIndex((item) => item.dataset.mediaId === active);
        if (index < 0) index = 0;
        currentMediaId = items[index]?.dataset.mediaId || null;
        controls.hidden = items.length < 2;
        count.textContent = `${index + 1}/${items.length}`;
        count.setAttribute('aria-label', `Productbeeld ${index + 1} van ${items.length}`);
        previous.disabled = index <= 0;
        next.disabled = index >= items.length - 1;
        if (dots.childElementCount !== items.length) {
          dots.replaceChildren(...items.map((_, itemIndex) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'bb-product-gallery__dot';
            dot.dataset.bbGalleryIndex = itemIndex;
            dot.setAttribute('aria-label', `Bekijk productbeeld ${itemIndex + 1} van ${items.length}`);
            return dot;
          }));
        }
        [...dots.children].forEach((dot, itemIndex) => {
          if (itemIndex === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const go = (step) => {
        const items = mediaItems();
        const index = items.findIndex((item) => item.dataset.mediaId === currentMediaId);
        const target = items[index + step];
        if (!target) return;
        gallery.setActiveMedia(target.dataset.mediaId, false);
        refresh(target.dataset.mediaId);
      };

      const onKeydown = (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        go(event.key === 'ArrowLeft' ? -1 : 1);
      };

      previous.addEventListener('click', () => go(-1));
      next.addEventListener('click', () => go(1));
      gallery.addEventListener('keydown', onKeydown);
      dots.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-bb-gallery-index]');
        const target = dot && mediaItems()[Number(dot.dataset.bbGalleryIndex)];
        if (!target) return;
        gallery.setActiveMedia(target.dataset.mediaId, false);
        refresh(target.dataset.mediaId);
      });
      gallery.querySelector('[id^="GalleryViewer-"]')?.addEventListener('slideChanged', (event) => {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        if (!mediaId) return;
        if (mediaId !== currentMediaId) window.pauseAllMedia?.();
        mediaItems().forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === mediaId));
        refresh(mediaId);
      });
      gallery.querySelector('.thumbnail-list')?.addEventListener('click', (event) => {
        const target = event.target.closest('[data-target]');
        if (target) refresh(target.dataset.target);
      });
      const observer = new MutationObserver(() => refresh());
      const mediaList = gallery.querySelector('.product__media-list');
      if (mediaList) observer.observe(mediaList, { childList: true });
      let scrollFrame = null;
      const onScroll = () => {
        if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = null;
          const items = mediaItems();
          const visibleLeft = mediaList.scrollLeft + 16;
          const activeItem = items.reduce((nearest, item) =>
            !nearest || Math.abs(item.offsetLeft - visibleLeft) < Math.abs(nearest.offsetLeft - visibleLeft)
              ? item
              : nearest, null);
          const mediaId = activeItem?.dataset.mediaId;
          if (!mediaId || mediaId === currentMediaId) return;
          window.pauseAllMedia?.();
          items.forEach((item) => item.classList.toggle('is-active', item === activeItem));
          refresh(mediaId);
        });
      };
      mediaList?.addEventListener('scroll', onScroll, { passive: true });
      const alignActiveOnResize = () => {
        const activeItem = mediaItems().find((item) => item.dataset.mediaId === currentMediaId);
        if (activeItem && mediaList) mediaList.scrollTo({ left: activeItem.offsetLeft - 16, behavior: 'instant' });
      };
      window.addEventListener('resize', alignActiveOnResize);
      refresh();
      return { gallery, refresh, disconnect: () => {
        observer.disconnect();
        mediaList?.removeEventListener('scroll', onScroll);
        if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
        gallery.removeEventListener('keydown', onKeydown);
        window.removeEventListener('resize', alignActiveOnResize);
      } };
    };

    galleryBinding = bindGallery(root.querySelector('media-gallery.bb-product-gallery'));
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== root.dataset.section) return;
        const gallery = root.querySelector('media-gallery.bb-product-gallery');
        if (gallery !== galleryBinding.gallery) {
          galleryBinding.disconnect();
          galleryBinding = bindGallery(gallery);
        } else {
          galleryBinding.refresh();
        }
      });
    }

    if (!bar) return;
    const button = bar.querySelector('[data-bb-mobile-submit]');
    const choice = bar.querySelector('[data-bb-mobile-choice]');
    const productName = bar.querySelector('[data-bb-mobile-product]');
    const treePrice = bar.querySelector('[data-bb-mobile-tree-price]');
    const depositNote = bar.querySelector('[data-bb-mobile-deposit-note]');
    const nativeButton = root.querySelector('product-form .product-form__submit');
    const form = root.querySelector('product-form form');
    if (!button || !nativeButton || !form) return;
    let variantPriceCents = Number(bar.dataset.variantPriceCents);
    const depositCents = Number(bar.dataset.depositCents);
    const number = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const depositNumber = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { maximumFractionDigits: 2 });
    const quantity = () => Math.max(1, Number(root.querySelector('input[name="quantity"]')?.value) || 1);

    const syncButton = () => {
      if (treePrice && Number.isFinite(variantPriceCents)) treePrice.textContent = number.format(((variantPriceCents - depositCents) * quantity()) / 100);
      if (depositNote) depositNote.textContent = `+ ${depositNumber.format((depositCents * quantity()) / 100)} borg bij afrekenen · terug bij inleveren`;
      button.disabled = nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true' || nativeButton.classList.contains('loading');
      const nativeLabel = nativeButton.querySelector('span');
      if (!button.disabled && nativeLabel) nativeLabel.textContent = 'In winkelwagen';
      button.querySelector('span').textContent = button.disabled ? nativeLabel?.textContent.trim() || 'Niet beschikbaar' : 'In winkelwagen';
    };
    new MutationObserver(syncButton).observe(nativeButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
    root.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quantity"]')) syncButton();
    });
    syncButton();

    const syncVisibility = () => {
      bar.hidden = !compact.matches && nativeButton.getBoundingClientRect().bottom > 100;
    };
    window.addEventListener('scroll', syncVisibility, { passive: true });
    window.addEventListener('resize', syncVisibility);
    compact.addEventListener('change', syncVisibility);
    syncVisibility();

    button.addEventListener('click', () => {
      if (!button.disabled) form.requestSubmit(nativeButton);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== root.dataset.section) return;
        if (data.variant?.price != null) {
          variantPriceCents = Number(data.variant.price);
          if (choice && data.variant.options?.[0]) choice.textContent = data.variant.options[0].replaceAll('*', '');
        }
        if (productName) productName.textContent = root.querySelector('.bb-product-heading h1')?.textContent.trim() || productName.textContent;
        syncButton();
        placePicker();
        syncVisibility();
      });
    }
  }

  const initAll = () => document.querySelectorAll('product-info').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('product-info:loaded', (event) => init(event.target));
  document.addEventListener('shopify:section:load', initAll);
})();
